import React, {useCallback, useEffect, useState} from "react";
import s from "./usersTab.module.scss";
import {useDispatch, useSelector} from "react-redux";
import {globalDataSelector} from "../../../../../../redux/slices/globalDataSlice";
import useCustomSnackbar from "../../../../../../hooks/useCustomSnackbar";
import {useSnackbar} from "notistack";
import {Link, useSearchParams} from "react-router-dom";
import useGetSearchParams from "../../../../../../hooks/useGetSearchParams";
import useSetSearchParams from "../../../../../../hooks/useSetSearchParams";
import {useDebounce} from "../../../../../../hooks/useDebounce";
import {
    page,
    search,
} from "../../../../Statistics/IOS/IOSClicksStats/IOSClicksStats";
import {getTableDefaultParams} from "../../../../../../api/helpers/scripts";
import {
    INVALID_PAGE_ERROR,
    snackbar_error,
    snackbar_info, snackbar_success,
} from "../../../../../../utils/vars/staticVariables";
import EmptyList from "../../../../../../components/EmptyList/EmptyList";
import CustomSkeleton from "../../../../../../components/UiKit/CustomSkeleton/CustomSkeleton";
import MuiTooltip from "../../../../../../components/UiKit/MuiTooltip/MuiTooltip";
import SvgSelector from "../../../../../../components/SvgSelector/SvgSelector";
import Pagination from "../../../../../../components/Pagination/Pagination";
import {
    balancesOrderingList,
    params,
    teamsUsersOrderingList,
    usersOrderingList
} from "../../../../../../utils/vars/params";
import GradientBorder from "../../../../../../components/GradientBorder/GradientBorder";
import {
    fetchTechnicalUsersList,
    getImpersonateUser,
    managementSelector,
} from "../../../../../../redux/slices/managementSlice";
import {useTranslation} from "react-i18next";
import ParamsSort from "../../../../../../components/ParamsSort/ParamsSort";
import {convertDate} from "../../../../../../utils/helpers/formatTime";
import {
    fetchAllUsers,
    // fetchUsers,
    removeUserFromTeam,
    switchUserStatus,
    teamsSelector
} from "../../../../../../redux/slices/teamsSlice";
import {capitalizeWord} from "../../../../../../utils/helpers/capitalizeWord";
import UserTr from "./UserTr/UserTr";
import {teamActivateUserFullTeamError} from "../../../../../../utils/vars/errorMessages";
import {setCanTmAddCount, subscriptionSelector} from "../../../../../../redux/slices/subscriptionSlice";

const UsersTab = ({fetchUsers}) => {
    const {isCollapsedSidebar} = useSelector(globalDataSelector);
    const {usersList, usersListPending} = useSelector(teamsSelector);
    const {can_tm_add_count} = useSelector(subscriptionSelector);

    const dispatch = useDispatch();

    const showSnackbar = useCustomSnackbar();
    const {closeSnackbar} = useSnackbar();

    const [searchParams, setSearchParams] = useSearchParams();

    const {search: searchValue, page: pageValue} = useGetSearchParams();

    const updateSearchParams = useSetSearchParams(setSearchParams);

    const {t} = useTranslation();

    const [name, setName] = useDebounce((value) => {
        if (value) {
            updateSearchParams(search, value);
        } else {
            updateSearchParams(search, value, true);
        }
    }, 600);

    useEffect(() => {
        if (!searchValue) {
            setName("");
        } else {
            setName(searchValue);
        }
    }, [searchParams]);

    useEffect(() => {
        const promise = fetchUsers();

        return () => {
            promise.abort();
        };
    }, [dispatch, searchParams]);


    const onSwitchUserStatusHandler = useCallback(
        async (subscription_service_id, is_active) => {
            const pendingDeleteSnackbar = showSnackbar(
                t(is_active ? 'user_archiving' : "user_activation"),
                snackbar_info,
                true,
            );

            const archiveUserResult = await dispatch(switchUserStatus(subscription_service_id)).unwrap();

            const {data, status} = archiveUserResult

            if (status === 200) {
                fetchUsers();
                if (is_active) {
                    dispatch(setCanTmAddCount(can_tm_add_count - 1))
                }
                closeSnackbar(pendingDeleteSnackbar);
                showSnackbar(t(is_active ? "user_archived" : "user_activated"), snackbar_success);
            } else if (data?.detail === teamActivateUserFullTeamError) {
                closeSnackbar(pendingDeleteSnackbar);
                showSnackbar(t("subscription_activate_user_error"), snackbar_error);
            } else {
                closeSnackbar(pendingDeleteSnackbar);
                showSnackbar(t(is_active ? "failed_archive_user" : 'failed_activate_user'), snackbar_error);
            }
        },
        [closeSnackbar, dispatch, fetchUsers, showSnackbar],
    );


    const onRemoveUserHandler = useCallback(
        async (subscription_service_id) => {
            const pendingDeleteSnackbar = showSnackbar(
                t("notif_delete_flow"),
                snackbar_info,
                true,
            );

            const removeTeamResult = await dispatch(removeUserFromTeam(subscription_service_id)).unwrap();

            const {status} = removeTeamResult

            if (status === 200) {
                fetchUsers();
                dispatch(setCanTmAddCount(can_tm_add_count - 1))
                closeSnackbar(pendingDeleteSnackbar);
                showSnackbar(t("user_removed"), snackbar_success);
            } else {
                closeSnackbar(pendingDeleteSnackbar);
                showSnackbar(t("failed_remove_user"), snackbar_error);
            }
        },
        [closeSnackbar, dispatch, fetchUsers, showSnackbar],
    );

    const trBlock = <tr>
        {teamsUsersOrderingList.map(({ordering, name}, index) => (
            <th key={index}>
                <div className={s.headerContent}>
                    {t(name)}
                    {
                        ordering && <ParamsSort
                            sortBy={ordering}
                            searchParams={searchParams}
                            setSearchParams={setSearchParams}
                        />
                    }
                </div>
            </th>
        ))}
    </tr>

    return (
        <div className={s.usersTableWrapper}>
            <GradientBorder className={s.gradientBorderCustom}/>

            <div className={s.pageInnerContent}>
                <div className={s.innerContentContainer}>
                    {usersList.length === 0 && !usersListPending ? (
                        <EmptyList/>
                    ) : usersListPending ? (
                        <section className={s.body}>
                            <table>
                                {trBlock}
                            </table>
                            <div className={s.skeletonWrapper}>
                                <CustomSkeleton styles={s.skeletonTable}/>
                            </div>
                        </section>
                    ) : (
                        <section className={s.body}>
                            <table>
                                <thead>
                                {trBlock}
                                </thead>
                                <tbody>
                                {usersList?.map((user) => {
                                    return (
                                        <UserTr
                                            {...user}
                                            key={user?.id}
                                            onSwitchUserStatusHandler={onSwitchUserStatusHandler}
                                            onRemoveUserHandler={onRemoveUserHandler}
                                        />
                                    );
                                })}
                                </tbody>
                            </table>
                        </section>
                    )}
                </div>
            </div>

            <div
                className={`${s.paginationContainer} ${isCollapsedSidebar === "true" ? s.collapsed : ""}`}
            >
                <div className={s.newRecordsCount}>
                    {t("total")}:{" "}
                    {!usersListPending && <span>{usersList.length}</span>}
                </div>
            </div>
        </div>
    );
};

export default UsersTab;
